/**
 * Super Admin — Gerenciar Helps (pool global de colaboradores do talent_bank).
 * Buscar, editar informações + raio de alcance, resetar senha do portal,
 * ativar/inativar e excluir qualquer Help (cross-tenant).
 *
 * Endpoints (bypass tenant scope no backend):
 *   GET    /super-admin/talent-bank?q=&status=
 *   PATCH  /super-admin/talent-bank/{id}
 *   POST   /super-admin/talent-bank/{id}/reset-password
 *   PATCH  /super-admin/talent-bank/{id}/status
 *   DELETE /super-admin/talent-bank/{id}
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Search, RefreshCw, Save, X as XIcon, Pencil, Trash2, KeyRound, Power,
  Star, MapPin, Phone, Mail, Car, Camera, Loader2, ShieldCheck, ShieldOff,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "../lib/api";
import { useConfirm } from "../hooks/useConfirm";

const LANGS = ["Português", "Inglês", "Espanhol", "Crioulo", "Francês"];
const AVAIL = ["Integral", "Meio período", "Fins de semana", "Noites", "Flexível"];
const WEEKDAYS = [
  { id: 0, short: "Dom" }, { id: 1, short: "Seg" }, { id: 2, short: "Ter" },
  { id: 3, short: "Qua" }, { id: 4, short: "Qui" }, { id: 5, short: "Sex" },
  { id: 6, short: "Sáb" },
];

const STATUS_BADGE = {
  disponivel: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  ocupado: "bg-amber-500/20 text-amber-200 border-amber-500/40",
  inativo: "bg-red-500/20 text-red-200 border-red-500/40",
};
const STATUS_LABEL = { disponivel: "Disponível", ocupado: "Ocupado", inativo: "Inativo" };

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";
}

export default function SuperAdminTalents() {
  const confirm = useConfirm();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/super-admin/talent-bank", { params });
      setRows(data.talents || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function toggleStatus(row) {
    const next = row.status === "inativo" ? "disponivel" : "inativo";
    const ok = await confirm({
      title: next === "inativo" ? `Inativar "${row.full_name}"?` : `Reativar "${row.full_name}"?`,
      description:
        next === "inativo"
          ? "Ele deixará de aparecer para as empresas na disponibilidade e no mapa."
          : "Ele voltará a aparecer como disponível para as empresas.",
      actionLabel: next === "inativo" ? "Inativar" : "Reativar",
      destructive: next === "inativo",
    });
    if (!ok) return;
    try {
      await api.patch(`/super-admin/talent-bank/${row.id}/status`, { status: next });
      toast.success(next === "inativo" ? "Help inativado" : "Help reativado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  async function doDelete(row) {
    const ok = await confirm({
      title: `Excluir "${row.full_name}"?`,
      description: "Esta ação é definitiva — o cadastro será removido do banco global de Helps.",
      destructive: true,
      actionLabel: "Excluir permanentemente",
      requireText: row.full_name,
    });
    if (!ok) return;
    try {
      await api.delete(`/super-admin/talent-bank/${row.id}`);
      toast.success(`"${row.full_name}" excluído`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  const counts = {
    total: rows.length,
    inativo: rows.filter((r) => r.status === "inativo").length,
    comSenha: rows.filter((r) => r.has_password).length,
  };

  return (
    <div className="space-y-5" data-testid="super-admin-talents-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Gerenciar Helps{" "}
            <span className="text-violet-300/80 font-normal">(pool global)</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            <b>{counts.total}</b> Help{counts.total !== 1 && "s"} · {counts.comSenha} com acesso ao portal ·{" "}
            {counts.inativo} inativo{counts.inativo !== 1 && "s"}. Edite dados, raio, senha, status e exclua.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm font-semibold text-slate-200"
          title="Recarregar"
          data-testid="talents-reload"
        >
          <RefreshCw size={14} />
        </button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr,220px] gap-2">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar por nome, cidade, e-mail ou telefone… (Enter)"
            data-testid="talents-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-white/10 bg-white/5 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="talents-filter-status"
          className="px-3 py-2.5 rounded-lg border border-white/10 bg-slate-900 text-sm text-white"
        >
          <option value="">Todos os status</option>
          <option value="disponivel">Disponível</option>
          <option value="ocupado">Ocupado</option>
          <option value="inativo">Inativo</option>
        </select>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400 bg-white/5 border border-white/10 rounded-xl">
          Carregando…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-slate-400 bg-white/5 border border-white/10 rounded-xl" data-testid="talents-empty">
          Nenhum Help encontrado.
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="divide-y divide-white/5">
            {rows.map((r) => (
              <TalentRow
                key={r.id}
                row={r}
                onEdit={() => setEditing(r)}
                onReset={() => setResetting(r)}
                onToggle={() => toggleStatus(r)}
                onDelete={() => doDelete(r)}
              />
            ))}
          </div>
        </div>
      )}

      {editing && (
        <EditModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      {resetting && (
        <ResetPasswordModal
          row={resetting}
          onClose={() => setResetting(null)}
          onDone={() => {
            setResetting(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function TalentRow({ row, onEdit, onReset, onToggle, onDelete }) {
  const isInactive = row.status === "inativo";
  return (
    <div
      className="px-4 py-3 grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-3 items-center hover:bg-white/5"
      data-testid={`talent-row-${row.id}`}
    >
      <div className="flex items-start gap-3 min-w-0">
        {row.photo_path ? (
          <img
            src={fileUrl(row.photo_path)}
            alt={row.full_name}
            className="w-10 h-10 rounded-lg object-cover border border-white/10 flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 text-slate-200 flex items-center justify-center flex-shrink-0 text-xs font-bold">
            {initials(row.full_name)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-white truncate flex items-center gap-2 flex-wrap">
            {row.full_name}
            <span className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border ${STATUS_BADGE[row.status] || STATUS_BADGE.disponivel}`}>
              {STATUS_LABEL[row.status] || row.status}
            </span>
            {row.rating ? (
              <span className="text-[11px] inline-flex items-center gap-0.5 text-amber-300 font-semibold">
                <Star size={11} className="fill-amber-300" /> {Number(row.rating).toFixed(1)}
              </span>
            ) : null}
            <span
              className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${
                row.has_password
                  ? "bg-sky-500/15 text-sky-200 border-sky-500/40"
                  : "bg-slate-700/60 text-slate-300 border-white/10"
              }`}
              title={row.has_password ? "Já criou senha do portal" : "Sem senha (não acessou o portal)"}
            >
              {row.has_password ? <ShieldCheck size={10} /> : <ShieldOff size={10} />}
              {row.has_password ? "Portal ativo" : "Sem senha"}
            </span>
          </div>
          <div className="text-[11px] text-slate-400 flex items-center gap-3 mt-0.5 flex-wrap">
            {row.city && <span className="flex items-center gap-1"><MapPin size={10} /> {row.city}{row.state ? `, ${row.state}` : ""}</span>}
            {row.email && <span className="flex items-center gap-1 truncate"><Mail size={10} /> {row.email}</span>}
            {row.phone && <span className="flex items-center gap-1"><Phone size={10} /> {row.phone}</span>}
            {row.has_car && <span className="flex items-center gap-1 text-emerald-300"><Car size={10} /> Carro</span>}
            {row.travel_radius_miles ? (
              <span className="flex items-center gap-1 text-violet-200 font-semibold">Raio {row.travel_radius_miles} mi</span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-1.5 flex-wrap">
        <button
          onClick={onEdit}
          data-testid={`talent-edit-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25"
        >
          <Pencil size={12} /> Editar
        </button>
        <button
          onClick={onReset}
          data-testid={`talent-reset-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-sky-500/40 bg-sky-500/15 text-sky-200 hover:bg-sky-500/25"
        >
          <KeyRound size={12} /> Senha
        </button>
        <button
          onClick={onToggle}
          data-testid={`talent-toggle-${row.id}`}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border ${
            isInactive
              ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
              : "border-amber-500/40 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"
          }`}
        >
          <Power size={12} /> {isInactive ? "Reativar" : "Inativar"}
        </button>
        <button
          onClick={onDelete}
          data-testid={`talent-delete-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-red-500/40 bg-red-500/15 text-red-200 hover:bg-red-500/25"
        >
          <Trash2 size={12} /> Excluir
        </button>
      </div>
    </div>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const [f, setF] = useState({
    full_name: row.full_name || "", phone: row.phone || "", email: row.email || "",
    address: row.address || "", city: row.city || "", state: row.state || "",
    zip_code: row.zip_code || "", has_license: !!row.has_license, has_car: !!row.has_car,
    vehicle_type: row.vehicle_type || "", languages: row.languages || [],
    work_days: row.work_days || [], availability: row.availability || "",
    experience: row.experience || "", travel_radius_miles: row.travel_radius_miles || 30,
    photo_path: row.photo_path || "", status: row.status || "disponivel",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Envie uma imagem");
    if (file.size > 10 * 1024 * 1024) return toast.error("Imagem muito grande (máx. 10MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_path", data.path);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const toggleLang = (l) =>
    setF((p) => ({
      ...p,
      languages: p.languages.includes(l) ? p.languages.filter((x) => x !== l) : [...p.languages, l],
    }));

  async function save() {
    if (!f.full_name.trim()) return toast.error("Informe o nome completo");
    setSaving(true);
    try {
      const payload = {
        full_name: f.full_name, phone: f.phone, email: f.email || null, address: f.address,
        city: f.city, state: f.state, zip_code: f.zip_code, has_license: f.has_license,
        has_car: f.has_car, vehicle_type: f.vehicle_type, languages: f.languages,
        work_days: f.work_days, availability: f.availability, experience: f.experience,
        travel_radius_miles: f.travel_radius_miles, photo_path: f.photo_path, status: f.status,
      };
      await api.patch(`/super-admin/talent-bank/${row.id}`, payload);
      toast.success("Help atualizado");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full text-white max-h-[90vh] overflow-y-auto" data-testid="talent-edit-modal">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10 sticky top-0 bg-slate-900 z-10">
          <div>
            <div className="text-xs uppercase tracking-wider text-violet-300 font-semibold">Editar Help</div>
            <h2 className="text-lg font-bold text-white">{f.full_name || "(sem nome)"}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400"><XIcon size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="flex items-center gap-3">
            {f.photo_path ? (
              <img src={fileUrl(f.photo_path)} alt="foto" className="w-16 h-16 rounded-xl object-cover border border-white/10" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-500"><Camera size={22} /></div>
            )}
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm font-medium cursor-pointer" data-testid="talent-edit-photo-btn">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
              {uploading ? "Enviando…" : f.photo_path ? "Trocar foto" : "Adicionar foto"}
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} disabled={uploading} data-testid="talent-edit-photo-input" />
            </label>
            {f.photo_path && (
              <button type="button" onClick={() => set("photo_path", "")} className="text-xs text-red-300 hover:underline">Remover</button>
            )}
          </div>

          <Field label="Nome completo">
            <input value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="talent-edit-name" className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefone"><input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="talent-edit-phone" className={inputCls} /></Field>
            <Field label="E-mail"><input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="talent-edit-email" className={inputCls} /></Field>
          </div>
          <Field label="Endereço"><input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="talent-edit-address" className={inputCls} /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Cidade"><input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="talent-edit-city" className={inputCls} /></Field>
            <Field label="Estado"><input value={f.state} onChange={(e) => set("state", e.target.value)} className={inputCls} /></Field>
            <Field label="CEP"><input value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} className={inputCls} /></Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 accent-violet-600" checked={f.has_license} onChange={(e) => set("has_license", e.target.checked)} /> Possui Driver
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 accent-violet-600" checked={f.has_car} onChange={(e) => set("has_car", e.target.checked)} /> Possui carro
            </label>
          </div>
          {f.has_car && (
            <Field label="Tipo de veículo"><input value={f.vehicle_type} onChange={(e) => set("vehicle_type", e.target.value)} className={inputCls} placeholder="Sedan, SUV, Van" /></Field>
          )}

          <Field label="Idiomas">
            <div className="flex flex-wrap gap-2">
              {LANGS.map((l) => (
                <button type="button" key={l} onClick={() => toggleLang(l)}
                  className={`px-2.5 py-1.5 rounded-full text-xs border transition ${f.languages.includes(l) ? "bg-violet-600 text-white border-violet-600" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                  {l}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Disponibilidade">
              <select value={f.availability} onChange={(e) => set("availability", e.target.value)} className={inputCls}>
                <option value="">Selecione…</option>
                {AVAIL.map((a) => <option key={a} value={a} className="bg-slate-900">{a}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="talent-edit-status" className={inputCls}>
                <option value="disponivel" className="bg-slate-900">Disponível</option>
                <option value="ocupado" className="bg-slate-900">Ocupado</option>
                <option value="inativo" className="bg-slate-900">Inativo</option>
              </select>
            </Field>
          </div>

          <Field label={`Raio de alcance — ${f.travel_radius_miles || 30} mi`}>
            <div className="flex items-center gap-3">
              <input type="range" min="5" max="100" step="5" value={f.travel_radius_miles || 30}
                onChange={(e) => set("travel_radius_miles", Number(e.target.value))}
                data-testid="talent-edit-radius" className="flex-1 accent-violet-600" />
              <span className="w-16 text-center text-sm font-semibold text-violet-200 bg-violet-500/15 rounded-lg py-1" data-testid="talent-edit-radius-value">
                {f.travel_radius_miles || 30} mi
              </span>
            </div>
          </Field>

          <Field label="Dias de trabalho">
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((w) => {
                const active = f.work_days.includes(w.id);
                return (
                  <button type="button" key={w.id}
                    onClick={() => setF((p) => ({
                      ...p,
                      work_days: p.work_days.includes(w.id)
                        ? p.work_days.filter((x) => x !== w.id)
                        : [...p.work_days, w.id].sort((a, b) => a - b),
                    }))}
                    className={`w-12 h-10 rounded-lg text-xs font-semibold border transition ${active ? "bg-violet-600 text-white border-violet-600" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                    {w.short}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Experiência">
            <textarea value={f.experience} onChange={(e) => set("experience", e.target.value)} className={`${inputCls} min-h-[80px]`} data-testid="talent-edit-experience" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10 sticky bottom-0 bg-slate-900">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="talent-edit-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-sm font-semibold disabled:opacity-50">
            <Save size={14} /> Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ row, onClose, onDone }) {
  const [mode, setMode] = useState("set"); // "set" | "clear"
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      if (mode === "set") {
        if (password.trim().length < 6) {
          setBusy(false);
          return toast.error("A senha deve ter ao menos 6 caracteres");
        }
        await api.post(`/super-admin/talent-bank/${row.id}/reset-password`, { mode: "set", password: password.trim() });
        toast.success(`Senha definida para "${row.full_name}": ${password.trim()}`, { duration: 12000 });
      } else {
        await api.post(`/super-admin/talent-bank/${row.id}/reset-password`, { mode: "clear" });
        toast.success("Senha removida — o Help fará o 1º acesso no portal.");
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-md w-full text-white" data-testid="talent-reset-modal">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <div className="text-xs uppercase tracking-wider text-sky-300 font-semibold">Resetar senha do portal</div>
            <h2 className="text-lg font-bold text-white">{row.full_name}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400"><XIcon size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setMode("set")}
              data-testid="reset-mode-set"
              className={`px-3 py-3 rounded-xl text-sm font-semibold border transition ${mode === "set" ? "bg-sky-500/20 text-sky-100 border-sky-500/50" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}
            >
              Definir nova senha
            </button>
            <button
              onClick={() => setMode("clear")}
              data-testid="reset-mode-clear"
              className={`px-3 py-3 rounded-xl text-sm font-semibold border transition ${mode === "clear" ? "bg-amber-500/20 text-amber-100 border-amber-500/50" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}
            >
              Limpar (1º acesso)
            </button>
          </div>

          {mode === "set" ? (
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-1 block">Nova senha (mín. 6)</label>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ex.: help1234"
                data-testid="reset-password-input"
                className={`${inputCls} font-mono`}
              />
              <p className="text-xs text-slate-400 mt-2">Você poderá copiar e enviar esta senha ao Help.</p>
            </div>
          ) : (
            <p className="text-sm text-slate-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              A senha atual será removida. O Help precisará abrir o portal e usar
              <b> "Primeiro acesso"</b> (e-mail + telefone do cadastro) para criar uma nova senha.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold">Cancelar</button>
          <button onClick={submit} disabled={busy} data-testid="reset-submit"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-sm font-semibold disabled:opacity-50">
            <KeyRound size={14} /> {mode === "set" ? "Definir senha" : "Limpar senha"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-violet-500 focus:outline-none";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-300 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
